<script setup>
/**
 * @fileoverview The Custom Mosaic specs sheet, shown instead of the tool at
 * `?print=1` and rendered to PDF by pdf.fireclaytile.com (utils/handOff.js).
 *
 * Everything comes from the share-link params, and colors use the hex stored
 * in the link (not a live Algolia lookup), so the PDF matches what the
 * shopper saw without waiting on any network call.
 *
 * @component
 */
import { computed } from 'vue';
import { usePrintSheet } from '../../../shared/utils/usePrintSheet.js';
import { qrCode } from '../../../shared/utils/qrCode.js';
import { useMosaicStore } from '../stores/mosaicStore';
import { CONSULTATION_URL } from '../utils/handOff.js';
import FireclayTileLogoHorizontal from './Shared/FireclayTileLogoHorizontal.vue';
import MosaicDrawing from './MosaicDrawing.vue';
import PartsList from './PartsList.vue';

/** The light margin a QR code needs around it to scan, in modules. */
const QR_QUIET_ZONE = 4;
const FIELD_REPEAT = 3;

usePrintSheet();

const store = useMosaicStore();
const pattern = computed(() => store.selectedPattern);

const generatedOn = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
});

/** Tile id → stored hex */
const fills = computed(() => {
    const out = {};
    for (const [id, c] of Object.entries(store.colors)) out[id] = c.hex;
    return out;
});

/** Glazes used on one sheet, most-used first */
const glazesUsed = computed(() => {
    const byName = new Map();
    for (const c of Object.values(store.colors)) {
        const entry = byName.get(c.name) || { name: c.name, hex: c.hex, count: 0 };
        entry.count += 1;
        byName.set(c.name, entry);
    }
    return [...byName.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
});

const unpaintedCount = computed(
    () => (pattern.value?.tileCount || 0) - Object.keys(store.colors).length,
);

/** Share link without the print flag */
const link = computed(() => store.shareUrl);

const qr = computed(() => (link.value ? qrCode(link.value) : null));

const consultationLabel = CONSULTATION_URL.replace(/^https?:\/\/(www\.)?/, '');
</script>

<template>
    <article
        data-testid="spec-sheet"
        class="mx-auto max-w-[800px] min-h-screen print:min-h-0 bg-white text-black font-body p-6 print:p-0 flex flex-col gap-4 print:gap-3"
    >
        <header class="flex items-center justify-between gap-4 pb-3 border-b border-black">
            <FireclayTileLogoHorizontal aria-label="Fireclay Tile" class="w-[140px] h-auto" />
            <h1 class="text-2xl font-header mb-0">Custom Mosaic Specs</h1>
        </header>

        <p v-if="!pattern" class="text-sm">
            This link doesn't name a Custom Mosaic. Design one at fireclaytile.com.
        </p>
        <template v-else>
            <section class="flex items-start justify-between gap-6">
                <dl class="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm m-0">
                    <dt class="font-bold">Pattern</dt>
                    <dd class="m-0">{{ pattern.name }}</dd>
                    <dt class="font-bold">Sheet size</dt>
                    <dd class="m-0">{{ pattern.width }}" × {{ pattern.height }}"</dd>
                    <dt class="font-bold">Tiles per sheet</dt>
                    <dd class="m-0">{{ pattern.tileCount }}</dd>
                    <dt class="font-bold">Material</dt>
                    <dd class="m-0">Original Ceramic</dd>
                    <dt class="font-bold">Grout</dt>
                    <dd class="m-0">
                        {{ store.groutColor?.name }} ({{ store.groutColor?.code }}), 3/16" joint
                    </dd>
                    <dt class="font-bold">Generated</dt>
                    <dd class="m-0">{{ generatedOn }}</dd>
                </dl>
                <figure v-if="qr" class="flex flex-col items-center gap-1 shrink-0 m-0">
                    <svg
                        data-testid="spec-qr-code"
                        role="img"
                        aria-label="QR code linking to this custom mosaic"
                        class="w-24 h-24"
                        :viewBox="`${-QR_QUIET_ZONE} ${-QR_QUIET_ZONE} ${qr.size + 2 * QR_QUIET_ZONE} ${qr.size + 2 * QR_QUIET_ZONE}`"
                        shape-rendering="crispEdges"
                    >
                        <path :d="qr.path" fill="#000" />
                    </svg>
                    <figcaption class="text-[10px]">Scan to open this design</figcaption>
                </figure>
            </section>

            <section class="grid grid-cols-2 gap-4 break-inside-avoid">
                <figure class="m-0 flex flex-col gap-1">
                    <MosaicDrawing
                        data-testid="spec-drawing-sheet"
                        role="img"
                        :aria-label="`${pattern.name}, one sheet`"
                        :pattern="pattern"
                        :fills="fills"
                        :grout-hex="store.groutColor?.hex"
                        class="w-full aspect-square"
                    />
                    <figcaption class="text-xs text-center">One sheet</figcaption>
                </figure>
                <figure class="m-0 flex flex-col gap-1">
                    <MosaicDrawing
                        data-testid="spec-drawing-field"
                        role="img"
                        :aria-label="`${pattern.name}, ${FIELD_REPEAT} by ${FIELD_REPEAT} sheets`"
                        :pattern="pattern"
                        :fills="fills"
                        :grout-hex="store.groutColor?.hex"
                        :repeat="FIELD_REPEAT"
                        class="w-full aspect-square"
                    />
                    <figcaption class="text-xs text-center">
                        {{ FIELD_REPEAT }} × {{ FIELD_REPEAT }} sheets
                    </figcaption>
                </figure>
            </section>

            <section aria-labelledby="cm-sheet-glazes" class="break-inside-avoid">
                <h2 id="cm-sheet-glazes" class="text-base font-bold mb-1">Glazes</h2>
                <ul v-if="glazesUsed.length" class="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                    <li v-for="g in glazesUsed" :key="g.name" class="flex items-center gap-2">
                        <span
                            class="inline-block w-4 h-4 border border-black"
                            :style="{ backgroundColor: g.hex }"
                            aria-hidden="true"
                        />
                        <span>{{ g.name }}</span>
                        <span class="ml-auto">{{ g.count }} tiles</span>
                    </li>
                </ul>
                <p v-if="unpaintedCount > 0" class="text-sm mt-1 mb-0">
                    {{ unpaintedCount }} of {{ pattern.tileCount }} tiles have no glaze chosen yet.
                </p>
            </section>

            <section aria-labelledby="cm-sheet-parts" class="break-inside-avoid">
                <h2 id="cm-sheet-parts" class="text-base font-bold mb-1">Parts list (per sheet)</h2>
                <PartsList />
            </section>

            <p class="text-xs break-all mb-0">
                Open this design:
                <a data-testid="spec-link" :href="link" class="underline">{{ link }}</a>
            </p>

            <footer class="mt-auto pt-2 border-t border-black text-xs flex flex-col gap-1">
                <p class="mb-0">
                    <strong>Ready to order this exact custom mosaic?</strong> Schedule a free design
                    consultation at {{ consultationLabel }}, call (800) 773-2226 or email
                    hello@fireclaytile.com.
                </p>
                <p class="mb-0">
                    Colors are approximate screen representations. Request samples to confirm glaze
                    and grout colors.
                </p>
            </footer>
        </template>
    </article>
</template>

<style>
@media print {
    @page {
        margin: 1cm;
    }
}
</style>
